import { useQuery } from '@tanstack/react-query';
import { useIdle } from '@uidotdev/usehooks';
import { useCallback, useMemo } from 'react';

import { ClientNetworkError } from '@/lib/errors/client';

import { getPriceSourceForIssuer } from '@/constants/issuers';

import type { Issuer } from '@/utils/0x/token-registry.types';

import { MATCHA_POLLING_IDLE_TIMEOUT_MS } from '../constants/polling';
import { REFRESH_INTERVAL } from '../constants/rpc';
import { DefinedApiUsdPriceResponse, Price } from '../utils/defined.types';
import { fetchWithJwt, getFullURL, handleResponse } from '../utils/fetcher';
import { routes } from '../utils/routes';

const INPUTS_LIMIT = 25;

/** Public input shape — callers pass token metadata (issuer included) and the
 * hook decides which provider to forward to via `getPriceSourceForIssuer`. */
export type UsdPriceInput = {
  address: string;
  networkId: number;
  timestamp?: number;
  issuer?: Issuer;
};

/** Normalised shape sent to /api/price/usd (and used in the query cache key). */
type ProxyInput = {
  address: string;
  networkId: number;
  timestamp?: number;
  source?: 'codex' | 'coingecko';
};

const toProxyInput = (input: UsdPriceInput): ProxyInput => {
  const proxy: ProxyInput = { address: input.address, networkId: input.networkId };
  if (input.timestamp !== undefined) proxy.timestamp = input.timestamp;
  // matcha-api rejects timestamp+source; for historical lookups we always
  // omit source so the call doesn't 400.
  if (input.timestamp === undefined) {
    const source = getPriceSourceForIssuer(input.issuer?.name);
    if (source) proxy.source = source;
  }
  return proxy;
};

export const usdPricesQueryKey = (proxyInputs: ProxyInput[]) =>
  ['usd-prices', proxyInputs] as const;

/**
 * Fetches USD prices for the given tokens via /api/price/usd, which proxies
 * matcha-api's `prices.spot` procedure.
 *
 * @param inputs - Tokens to price. Pass `issuer` when known so xStocks tokens
 *                 route to CoinGecko instead of the default provider.
 * @param watch - Refresh on each new block while the tab is active.
 */
export const useUSDPrices = (inputs: UsdPriceInput[] | undefined, watch = true) => {
  const isIdle = useIdle(MATCHA_POLLING_IDLE_TIMEOUT_MS);

  const proxyInputs = useMemo(() => (inputs?.length ? inputs.map(toProxyInput) : []), [inputs]);

  const usdPricesFetcher = useCallback(async (): Promise<DefinedApiUsdPriceResponse> => {
    if (!proxyInputs.length) return [];

    const clonedInputs = [...proxyInputs];
    const inputsGroups = [];
    while (clonedInputs.length > 0) {
      inputsGroups.push(clonedInputs.splice(0, INPUTS_LIMIT));
    }

    try {
      const responses = await Promise.all(
        inputsGroups.map((inputsBatch) =>
          fetchWithJwt(
            getFullURL(
              `${routes.api.USD_PRICES}?inputs=${encodeURIComponent(JSON.stringify(inputsBatch))}`,
            ),
          ).then(handleResponse<(Price | null)[]>),
        ),
      );

      return ([] as (Price | null)[]).concat(...responses);
    } catch (err) {
      if (err instanceof Error) throw err;
      throw new ClientNetworkError('Token results failed to fetch.');
    }
  }, [proxyInputs]);

  return useQuery<DefinedApiUsdPriceResponse, Error>({
    queryKey: usdPricesQueryKey(proxyInputs),
    queryFn: usdPricesFetcher,
    enabled: proxyInputs.length > 0,
    refetchInterval: watch ? (isIdle ? false : REFRESH_INTERVAL) : false,
    ...(isIdle ? { refetchOnReconnect: false } : {}),
  });
};
